import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { isWebHost } from "../webHost";
import { randomUUID } from "../lib/utils";
import {
  createBirthKeys,
  deskBirthGoesToWorld,
  deskBirthMacTools,
  isBirthMacRefusal,
  deskBirthRouteMatters,
  ROUTE_UNANSWERED_REASON,
  useDeskConversationRoute,
} from "./deskConversationRoute";
import {
  readRoomEntryAsPerson,
  readWorkroomAsPerson,
  refusalOfWorldError,
} from "./webHostWorldDoors";
import { birthWorkroomSessionInBrowser, openWorkroomConversation } from "./webHostWorkroomBirth";
import { activateOrCreateWorkroom, findWorkroom, type WorkroomDoorOutcome } from "./workroomDoor";
import { readWorldConvexClient } from "./worldConvexClient";
import { useWorldViewIfSeated } from "./worldView";
import { useNeucleosWorldStore } from "./worldStore";

export type WorkroomSessionBirthOutcome =
  | WorkroomDoorOutcome
  | {
      readonly ok: false;
      readonly kind: "unseated" | "busy" | "context_changed" | "world_unanswered";
      readonly reason: string;
    };

/** What the person meant: open the room where it stands, or start a fresh session. */
export type WorkroomSessionIntent = "resume" | "new";

/**
 * ONE door for a workroom's sessions (RM-CAN-09):
 * the dock chip resumes the room's latest session (creating the room and its
 * first session on first use); the seat's plus and the chooser's New start a
 * fresh session even when the previous one is empty. Both
 * revalidate admission through the room door, refuse while a birth is in
 * flight, and refuse to navigate when the World's identity or grant changed
 * underneath the request. The caller shows a refusal in its own voice.
 *
 * In the browser host (webHost.ts) the same door births the session in the World itself, as a conversation kept there
 * (webHostWorkroomBirth.ts): the World opens it under the Workroom's room, the page reads it back and navigates, and
 * Cleo answers in it through the World, with no Mac.
 *
 * On the Desk, a New does the same where the World's switch says so for that World and this Desk holds no folder
 * setting for it (deskConversationRoute.ts, one-backend plan step 3): the session is kept in the World, opened and read
 * back with the window's own sign-in. Everything else is the Mac's door, as before.
 */
export function useWorkroomSessionBirth(): {
  readonly birth: (input: {
    readonly workroomKey: string;
    readonly title: string;
    readonly intent: WorkroomSessionIntent;
  }) => Promise<WorkroomSessionBirthOutcome>;
  readonly busy: boolean;
} {
  const view = useWorldViewIfSeated();
  const viewRef = useRef(view);
  viewRef.current = view;
  const navigate = useNavigate();
  const pending = useRef(false);
  const alive = useRef(true);
  const [busy, setBusy] = useState(false);
  const { routeAt, routeSettledAt, folderSettingAt, macToolsAt, macToolsSettledAt } =
    useDeskConversationRoute(
      view !== null && view.state.kind === "world-ready" ? view.state.worldKey : null,
    );
  const birthKeys = useRef(createBirthKeys(randomUUID));
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const birth = useCallback(
    async ({
      workroomKey,
      title,
      intent,
    }: {
      workroomKey: string;
      title: string;
      intent: WorkroomSessionIntent;
    }) => {
      const current = viewRef.current;
      if (current === null || current.state.kind !== "world-ready")
        return {
          ok: false as const,
          kind: "unseated" as const,
          reason: "Sign in to a World before opening a room.",
        };
      if (pending.current)
        return {
          ok: false as const,
          kind: "busy" as const,
          reason: "A session is already opening.",
        };
      const { worldKey } = current.state;
      let index = current.state.index;
      const freshSession = intent === "new";
      const access = useNeucleosWorldStore.getState();
      const checkCurrent = () => {
        const now = useNeucleosWorldStore.getState();
        const displayed = viewRef.current?.state;
        if (
          !alive.current ||
          now.activeWorldKey !== worldKey ||
          displayed?.kind !== "world-ready" ||
          displayed.worldKey !== worldKey ||
          now.identity !== access.identity ||
          now.grantRevision !== access.grantRevision
        )
          throw new Error("World access changed. Try again.");
      };
      pending.current = true;
      setBusy(true);
      const finishWait =
        typeof current.beginWait === "function" ? current.beginWait(`Opening ${title}…`) : () => {};
      try {
        if (isWebHost())
          return await birthWorkroomSessionInBrowser({
            worldKey,
            index,
            workroomKey,
            title,
            intent,
            client: readWorldConvexClient(),
            clientKey: randomUUID(),
            acceptEntryReadback: current.acceptEntryReadback,
            navigate: ({ sessionId }) => {
              checkCurrent();
              return navigate({
                to: "/session/$sessionId",
                params: { sessionId },
                search: { world: worldKey },
              } as never);
            },
          });
        const pressedClient = readWorldConvexClient();
        let workroomKnown = findWorkroom(index, workroomKey) !== undefined;
        // A missing cache row may be a freshly created Workroom. Its route still matters:
        // wait for the usual answer, then confirm just that room before choosing a birth door.
        // Resume and another folder keep the Mac's door without this extra read.
        const settled = deskBirthRouteMatters({
          intent,
          folderSetting: folderSettingAt(worldKey),
          workroomKnown: true,
        })
          ? await routeSettledAt(worldKey)
          : { route: routeAt(pressedClient, worldKey), client: pressedClient };
        if (settled === "cancelled")
          return {
            ok: false as const,
            kind: "context_changed" as const,
            reason: "The active World or sign-in changed while opening the room.",
          };
        if (settled === "unanswered")
          return {
            ok: false as const,
            kind: "world_unanswered" as const,
            reason: ROUTE_UNANSWERED_REASON,
          };
        // The World's answer and the client it came through: a World birth uses exactly that client, never one read afterwards
        // (a client gone by then must not turn a World answer into a Mac birth; Codex review r3 of #3183).
        const { route, client } = settled;
        if (route === "world" && client === null)
          return {
            ok: false as const,
            kind: "world_unanswered" as const,
            reason: ROUTE_UNANSWERED_REASON,
          };
        checkCurrent();
        if (
          !workroomKnown &&
          client !== null &&
          intent === "new" &&
          route === "world" &&
          (folderSettingAt(worldKey) === "none" || folderSettingAt(worldKey) === "explicit-here")
        ) {
          const confirmed = await readWorkroomAsPerson(client, { worldKey, workroomKey });
          checkCurrent();
          if (!confirmed.ok) return confirmed;
          if (confirmed.room !== null) {
            index = { ...index, rooms: [...index.rooms, confirmed.room] };
            workroomKnown = true;
          }
        }
        const folderBeforeWait = folderSettingAt(worldKey);
        // A folder birth waits, once and briefly, for this Mac's switch when it has not been read yet; any other press decides now.
        const macTools =
          intent === "new" && folderBeforeWait === "explicit-here" && route === "world"
            ? await macToolsSettledAt(client, worldKey)
            : macToolsAt(client, worldKey);
        checkCurrent();
        const folderSetting = folderSettingAt(worldKey);
        if (
          deskBirthGoesToWorld({
            intent,
            route,
            folderSetting,
            workroomKnown,
            macTools,
          }) &&
          client !== null
        ) {
          // A folder conversation records this Mac, so its folder tools use this Mac's folders (steps 5-7 design §2.3).
          const birthMac = deskBirthMacTools({ folderSetting, macTools });
          const clientKey = birthKeys.current.take(worldKey, workroomKey, access.identity);
          let opened = false;
          const outcome = await birthWorkroomSessionInBrowser({
            worldKey,
            index,
            workroomKey,
            title,
            intent,
            client,
            clientKey,
            // The World may have opened the conversation even when its answer is lost or it fails afterwards, so the key is
            // kept unless the World said it wrote nothing (its refusals end "0 writes").
            open: async (at, args) => {
              try {
                const born = await openWorkroomConversation(at, {
                  ...args,
                  ...(birthMac ? { macTools: birthMac } : {}),
                });
                opened = true;
                return born;
              } catch (cause) {
                const reason = refusalOfWorldError(cause).body.reason;
                // A folder birth refused because it cannot be this Mac's lets its key go: the next press starts afresh.
                opened = !reason.includes("0 writes") && !isBirthMacRefusal(reason);
                throw cause;
              }
            },
            readEntry: (entry) =>
              entry.sessionId === undefined
                ? Promise.resolve({
                    ok: false as const,
                    kind: "invalid_room",
                    reason: "A room entry read names one session.",
                  })
                : readRoomEntryAsPerson(client, {
                    worldKey: entry.worldKey,
                    roomId: entry.roomId ?? "",
                    sessionId: entry.sessionId,
                  }),
            acceptEntryReadback: current.acceptEntryReadback,
            navigate: ({ sessionId }) => {
              checkCurrent();
              return navigate({
                to: "/session/$sessionId",
                params: { sessionId },
                search: { world: worldKey },
              } as never);
            },
          }).catch((cause: unknown) => {
            birthKeys.current.settle(worldKey, workroomKey, { ok: false, opened });
            throw cause;
          });
          birthKeys.current.settle(worldKey, workroomKey, { ok: outcome.ok, opened });
          return outcome;
        }
        return await activateOrCreateWorkroom({
          worldKey,
          index,
          workroomKey,
          title,
          freshSession,
          acceptReadback: current.acceptReadback,
          acceptEntryReadback: current.acceptEntryReadback,
          navigate: ({ sessionId }) => {
            checkCurrent();
            return navigate({
              to: "/session/$sessionId",
              params: { sessionId },
              search: { world: worldKey },
            } as never);
          },
        });
      } catch (cause) {
        return {
          ok: false as const,
          kind: "context_changed" as const,
          reason:
            cause instanceof Error
              ? cause.message
              : "The session could not be confirmed. Refresh before retrying.",
        };
      } finally {
        finishWait();
        pending.current = false;
        if (alive.current) setBusy(false);
      }
    },
    [navigate, routeAt, routeSettledAt, folderSettingAt, macToolsAt, macToolsSettledAt],
  );
  return { birth, busy };
}
